import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resendWelcomeEmail, Tenant, verifyResetPasswordToken } from '@steelbuy/api-integration';
import { isAccessDeniedError, isValidationError } from '@steelbuy/error';
import { ButtonCallToAction, CustomLink, FormActionbar, LoadingSpinnerOverlay } from '@steelbuy/ui-primitive';
import { ResetPasswordForm } from './ResetPasswordForm';
import { LoginLayout } from '../login-layout/LoginLayout';

import './ResetPassword.scss';

enum TokenError {
    EXPIRED = 1,
    UNKNOWN,
}

interface ResetPasswordProps {
    tenant: Tenant;
    apiBaseUrl: string;
    loginPath: string;
    forgotPasswordPath: string;
    isSetNewPassword?: boolean;
}

export const ResetPassword = ({
    tenant,
    apiBaseUrl,
    loginPath,
    forgotPasswordPath,
    isSetNewPassword = false,
}: ResetPasswordProps) => {
    const { t } = useTranslation('uiDomain');
    const classPrefix = 'reset-password';
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    const [error, setError] = useState<TokenError | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            navigate(loginPath);
            return;
        }
        (async () => {
            try {
                await verifyResetPasswordToken(apiBaseUrl, token);
            } catch (e) {
                if (isValidationError(e) && e?.message.includes('Token has expired please submit again')) {
                    setError(TokenError.EXPIRED);
                } else if (isAccessDeniedError(e) && e?.message.includes('Password already created with this token')) {
                    navigate(loginPath, {
                        state: {
                            defaultSuccessNotification: t('login.passwordAlreadyCreated'),
                        },
                    });
                } else {
                    setError(TokenError.UNKNOWN);
                }
            }
            setIsLoading(false);
        })();
    }, []);

    const onResendEmail = () => {
        if (error === TokenError.EXPIRED) {
            email && resendWelcomeEmail(apiBaseUrl, encodeURIComponent(email));
            navigate(loginPath, {
                state: {
                    defaultSuccessNotification: t('login.tempPasswordEmailSent', {
                        username: email,
                    }),
                },
            });
        } else {
            navigate(loginPath);
        }
    };

    const onSuccess = (username: string) => {
        navigate(loginPath, {
            state: {
                defaultSuccessNotification: isSetNewPassword
                    ? t('login.createNewPassword.successNotification')
                    : t('login.resetPassword.successNotification'),
                defaultUsername: username,
            },
        });
    };

    const errorLiteral = (() => {
        if (error === TokenError.EXPIRED) {
            return isSetNewPassword
                ? 'login.createNewPassword.expiredPassword.expiredTokenError'
                : 'login.resetPassword.expiredPassword.expiredTokenError';
        }
        return 'login.resetPassword.expiredPassword.unknownTokenError';
    })();

    const renderForm = () => {
        if (error) {
            return (
                <main className={`${classPrefix}__error`}>
                    <header className={`${classPrefix}__error--header`}>
                        <div>{t(`${errorLiteral}.header`)}</div>
                        <section className={`${classPrefix}__error--header--subheading`}>
                            <p>{t(`${errorLiteral}.body`)}</p>
                        </section>
                    </header>
                    <FormActionbar>
                        <ButtonCallToAction
                            label={
                                isSetNewPassword
                                    ? t('login.createNewPassword.expiredPassword.submitLabel')
                                    : t('login.resetPassword.expiredPassword.submitLabel')
                            }
                            onClick={() => {
                                if (isSetNewPassword) {
                                    onResendEmail();
                                } else {
                                    navigate(forgotPasswordPath);
                                }
                            }}
                        />
                    </FormActionbar>
                </main>
            );
        }
        return (
            token && (
                <ResetPasswordForm
                    token={token}
                    onSuccess={onSuccess}
                    forgotPasswordUrl={forgotPasswordPath}
                    tenant={tenant}
                    apiBaseUrl={apiBaseUrl}
                    isSetNewPassword={isSetNewPassword}
                    email={email ?? ''}
                />
            )
        );
    };

    return (
        <LoginLayout>
            <div className={classPrefix}>
                {isLoading ? (
                    <LoadingSpinnerOverlay />
                ) : (
                    <>
                        {renderForm()}
                        <div className={`${classPrefix}__link`}>
                            <CustomLink path={loginPath} isInlineLink>
                                {t('login.backToLogin')}
                            </CustomLink>
                        </div>
                    </>
                )}
            </div>
        </LoginLayout>
    );
};
